import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useMediaQuery } from '~/common/hooks/use-media-query';

import {
  WindowedGridInner,
  WindowedGridList,
  WindowedGridViewport,
} from './styles';

interface IPromptWindowedGridProps<TItem> {
  items: TItem[];
  renderItem: (item: TItem, index: number) => ReactNode;
  enabled?: boolean;
  scrollContainerId?: string;
  overscanRows?: number;
}

type TViewportSnapshot = {
  clientHeight: number;
  listTop: number;
  scrollTop: number;
};

const DEFAULT_VIEWPORT_HEIGHT = 900;

export const PromptWindowedGrid = <TItem,>({
  items,
  renderItem,
  enabled = true,
  scrollContainerId = 'mainlayoutcontainer',
  overscanRows = 3,
}: IPromptWindowedGridProps<TItem>) => {
  const listRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);
  const isThreeColumns = useMediaQuery('(max-width: 1260px)');
  const isTwoColumns = useMediaQuery('(max-width: 1030px)');
  const isMobile = useMediaQuery('(max-width: 580px)');

  const [viewport, setViewport] = useState<TViewportSnapshot>({
    clientHeight:
      typeof window !== 'undefined'
        ? window.innerHeight
        : DEFAULT_VIEWPORT_HEIGHT,
    listTop: 0,
    scrollTop: 0,
  });

  const columns = useMemo(() => {
    if (isTwoColumns) {
      return 2;
    }

    if (isThreeColumns) {
      return 3;
    }

    return 4;
  }, [isThreeColumns, isTwoColumns]);

  const gap = isMobile ? 12 : 20;
  const itemHeight = isMobile ? 260 : 320;
  const rowStride = itemHeight + gap;
  const totalRows = Math.ceil(items.length / columns);
  const totalHeight = totalRows > 0 ? totalRows * rowStride - gap : 0;

  const measureViewport = useCallback(() => {
    const scrollContainer = document.getElementById(scrollContainerId);
    const listElement = listRef.current;

    if (!scrollContainer || !listElement) {
      return;
    }

    const scrollContainerRect = scrollContainer.getBoundingClientRect();
    const listRect = listElement.getBoundingClientRect();

    setViewport({
      clientHeight: scrollContainer.clientHeight || DEFAULT_VIEWPORT_HEIGHT,
      listTop:
        listRect.top - scrollContainerRect.top + scrollContainer.scrollTop,
      scrollTop: scrollContainer.scrollTop,
    });
  }, [scrollContainerId]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const scrollContainer = document.getElementById(scrollContainerId);

    if (!scrollContainer) {
      return undefined;
    }

    const scheduleMeasure = () => {
      if (frameRef.current !== null) {
        return;
      }

      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null;
        measureViewport();
      });
    };

    measureViewport();
    scrollContainer.addEventListener('scroll', scheduleMeasure, {
      passive: true,
    });
    window.addEventListener('resize', scheduleMeasure);

    return () => {
      scrollContainer.removeEventListener('scroll', scheduleMeasure);
      window.removeEventListener('resize', scheduleMeasure);

      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [enabled, measureViewport, scrollContainerId]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    measureViewport();
  }, [columns, enabled, items.length, measureViewport]);

  const visibleRange = useMemo(() => {
    if (!enabled || items.length === 0) {
      return {
        startIndex: 0,
        endIndex: items.length,
        topPadding: 0,
        bottomPadding: 0,
      };
    }

    const visibleTop = Math.max(0, viewport.scrollTop - viewport.listTop);
    const visibleBottom = Math.max(
      visibleTop,
      viewport.scrollTop + viewport.clientHeight - viewport.listTop
    );

    const startRow = Math.max(
      0,
      Math.floor(visibleTop / rowStride) - overscanRows
    );
    const endRow = Math.min(
      totalRows,
      Math.ceil(visibleBottom / rowStride) + overscanRows
    );

    const startIndex = startRow * columns;
    const endIndex = Math.min(items.length, endRow * columns);
    const visibleRows = Math.ceil((endIndex - startIndex) / columns);
    const visibleHeight =
      visibleRows > 0 ? visibleRows * rowStride - gap : 0;
    const topPadding = startRow * rowStride;
    const bottomPadding = Math.max(
      0,
      totalHeight - topPadding - visibleHeight
    );

    return {
      startIndex,
      endIndex,
      topPadding,
      bottomPadding,
    };
  }, [
    columns,
    enabled,
    gap,
    items.length,
    overscanRows,
    rowStride,
    totalHeight,
    totalRows,
    viewport.clientHeight,
    viewport.listTop,
    viewport.scrollTop,
  ]);

  const visibleItems = useMemo(
    () => items.slice(visibleRange.startIndex, visibleRange.endIndex),
    [items, visibleRange.endIndex, visibleRange.startIndex]
  );

  return (
    <WindowedGridViewport ref={listRef}>
      <WindowedGridInner
        style={{
          paddingTop: visibleRange.topPadding,
          paddingBottom: visibleRange.bottomPadding,
        }}
      >
        <WindowedGridList $columns={columns} $gap={gap}>
          {visibleItems.map((item, index) =>
            renderItem(item, visibleRange.startIndex + index)
          )}
        </WindowedGridList>
      </WindowedGridInner>
    </WindowedGridViewport>
  );
};
